import { useCallback, useEffect, useState } from "react"

import { api, type ContaNaTela, type PapelNaTela } from "../api"
import { mensagemDeRecusa } from "../componentes/BlocoDeCampos"
import { Button } from "../componentes/Button"
import { Card, CardContent, CardHeader, CardTitle } from "../componentes/Card"
import { ComDica } from "../componentes/ComDica"
import * as Icones from "../componentes/Icones"
import { Skeleton } from "../componentes/Skeleton"
import { Column, TablePrincipal2 } from "../componentes/TablePrincipal2"
import { inteiro } from "../formato"
import { BOTAO_BRANCO, MAIUSCULA, RECUSA } from "../lib/classes"
import { cn } from "../lib/cn"
import { perfisDe } from "./Contas"

type LinhaDoPerfil = PapelNaTela & { contas: number | null; doServidor: boolean }

const DICA =
  "O perfil de cada conta se escolhe em Contas e permissões, e quem tem mais de um perfil soma as permissões. Só Gestor e Root veem Configurações."

const ROOT_DA_TELA: LinhaDoPerfil = {
  papel: "root",
  nome: "Root",
  pode: "Atende todas as entidades e alcança toda a configuração, inclusive o que vale para todas elas.",
  contas: null,
  doServidor: false,
}

export function PerfisDeAcesso({ aoAbrirContasDoPerfil }: { aoAbrirContasDoPerfil: (papel: string) => void }) {
  const [linhas, definirLinhas] = useState<LinhaDoPerfil[] | null>(null)
  const [recusa, definirRecusa] = useState<string | null>(null)

  const carregar = useCallback(() => {
    definirRecusa(null)
    api
      .contas()
      .then((resposta) => {
        const ativas: ContaNaTela[] = resposta.dados.filter((conta) => conta.ativo)
        const doServidor: LinhaDoPerfil[] = resposta.papeis.map((p) => ({
          ...p,
          contas: ativas.filter((conta) => (p.papel === "root" ? conta.is_root : perfisDe(conta).includes(p.papel))).length,
          doServidor: true,
        }))
        definirLinhas(doServidor.some((p) => p.papel === "root") ? doServidor : [...doServidor, ROOT_DA_TELA])
      })
      .catch((falha: unknown) => definirRecusa(mensagemDeRecusa(falha)))
  }, [])

  useEffect(carregar, [carregar])

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          Perfis de acesso <ComDica dica={DICA} />
        </CardTitle>
      </CardHeader>
      {recusa !== null ? (
        <CardContent className="flex flex-wrap items-center gap-3">
          <p role="alert" className={cn(RECUSA, MAIUSCULA)}>
            {recusa}
          </p>
          <Button type="button" variant="outline" size="sm" className={BOTAO_BRANCO} onClick={carregar}>
            <Icones.Recalcular />
            Tentar de novo
          </Button>
        </CardContent>
      ) : linhas === null ? (
        <CardContent aria-busy="true" className="flex flex-col gap-3">
          {[0, 1, 2, 3].map((linha) => (
            <Skeleton key={linha} className="h-9 w-full" />
          ))}
        </CardContent>
      ) : (
        <CardContent>
          <TablePrincipal2
            data={linhas}
            pageCount={1}
            currentPage={1}
            onPageChange={() => undefined}
            paginacao={false}
            listrada
            aoClicarNaLinha={(linha) => aoAbrirContasDoPerfil(linha.papel)}
            linhaClicavel={(linha) => linha.doServidor}
          >
            <Column<LinhaDoPerfil> name="nome" label="Perfil" enableSorting={false} render={(valor) => <strong>{String(valor)}</strong>} />
            <Column<LinhaDoPerfil>
              name="pode"
              label="O que pode"
              enableSorting={false}
              render={(valor) => (
                <span className={cn("inline-block text-(--tinta-2)", MAIUSCULA)}>{valor === null || valor === "" ? "—" : String(valor)}</span>
              )}
            />
            <Column<LinhaDoPerfil>
              name="contas"
              label="Contas"
              enableSorting={false}
              render={(valor) => <span className="tabular-nums">{valor === null ? "—" : inteiro(Number(valor))}</span>}
            />
          </TablePrincipal2>
        </CardContent>
      )}
    </Card>
  )
}
